import {
    useState, useRef, useCallback, useEffect
} from 'react';

/**
 * Hook to track if scroll position exceeds a threshold
 * Useful for triggering compact/expanded modes based on scroll position
 *
 * @param {number} threshold - Scroll position threshold in pixels (default: 50)
 * @param {number} headerHeight - Height of fixed header to account for when calculating if content can collapse (default: 0)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 50)
 * @returns {Object} Object containing scroll state and handlers
 * @returns {boolean} isScrolled - Whether scroll position exceeds threshold
 * @returns {Function} handleScroll - Scroll event handler
 * @returns {React.RefObject} scrollRef - Ref for the scrollable element
 *
 * @example
 * const { isScrolled, handleScroll, scrollRef } = useScrollThreshold(50, 0, 50);
 *
 * <div ref={scrollRef} onScroll={handleScroll}>
 *   {isScrolled ? 'Compact Mode' : 'Expanded Mode'}
 * </div>
 */
export const useScrollThreshold = (threshold = 50, headerHeight = 0, debounceMs = 50) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollRef = useRef(null);
    const lastScrollTopRef = useRef(0);
    const scrollTimeoutRef = useRef(null);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;

        if (!el) {
            return;
        }

        const currentScrollTop = el.scrollTop;

        // Always update the scroll position ref
        lastScrollTopRef.current = currentScrollTop;
        const canCollapse = el.scrollHeight > window.innerHeight - headerHeight;

        // Debounce the isScrolled state update to reduce re-renders
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            if (canCollapse) {
                setIsScrolled(currentScrollTop > threshold);
            } else {
                setIsScrolled(false);
            }
        }, debounceMs);
    }, [threshold, debounceMs]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return { isScrolled, handleScroll, scrollRef };
};

export default useScrollThreshold;
